"use strict"

import AssignmentExport from "../../../app/model/domain/assignment-export"
import { AssignmentSheetMetadata } from "../../../app/components/assignment-sheet/assignment-sheet.directive"
import ExportService from "../../../app/services/export/export.service"
import Assignment from "../../../app/model/domain/assignment"
import AssignmentWork from "../../../app/model/domain/assignment-work"
import AssignmentQuestion from "../../../app/model/domain/assignment-question"
import { UserRoles } from "../../../app/model/domain/user"
import { ElementIntents } from "../../../app/model/domain/element-metadata"

export class AssignmentExportService {
  /**
   * @ngInject
   */
  constructor(
    private $q: angular.IQService,
    private $document: angular.IDocumentService,
    private ExportService: ExportService,
  ) {}

  private getDocument(): Document {
    return this.$document[0] as Document
  }

  convertCurrentQuestionToBlob(): Promise<Blob> {
    const svgString = this.extractSVGString()
    return this.ExportService.svgStringToBlob(svgString)
  }

  convertCurrentQuestionToBlobWithOverlay(overlayCoords: {
    x: number
    y: number
  }): Promise<Blob> {
    const svgString = this.extractSVGString()
    return this.ExportService.svgStringToBlob(
      svgString,
      undefined,
      undefined,
      overlayCoords,
    )
  }

  convertAssignmentQuestionsToBlobs(
    assignment: Assignment,
    onQuestionRendered?: (config: AssignmentSheetMetadata) => void,
  ): Promise<Array<{ question: AssignmentQuestion; blob: Blob }>> {
    const assignmentExport = new AssignmentExport(assignment)
    assignmentExport.showMetadata = false

    return this.renderAssignmentToBlobs(assignmentExport, onQuestionRendered)
  }

  private async renderAssignmentToBlobs(
    assignmentExport: AssignmentExport,
    onQuestionRendered?: (config: AssignmentSheetMetadata) => void,
  ): Promise<Array<{ question: AssignmentQuestion; blob: Blob }>> {
    await this.loadMathJaxScript()

    const results: Array<{ question: AssignmentQuestion; blob: Blob }> = []
    for (const assignmentQuestion of assignmentExport.questions) {
      const { metadata, promise } = this.renderQuestion(
        assignmentExport.assignment,
        assignmentQuestion.id,
      )
      onQuestionRendered?.(metadata)
      await promise
      const svgString = this.extractSVGString({ svgIsHidden: true })
      const blob = await this.ExportService.svgStringToBlob(svgString)
      results.push({ question: assignmentQuestion, blob })
    }
    return results
  }

  private renderQuestion(
    assignment: Assignment | AssignmentWork,
    questionId: string,
  ): {
    metadata: AssignmentSheetMetadata
    promise: Promise<void>
  } {
    let resolvePromise: () => void
    const promise = new Promise<void>((resolve) => {
      resolvePromise = resolve
    })

    const metadata = new AssignmentSheetMetadata(
      assignment,
      questionId,
      true,
      true,
      undefined,
      UserRoles.TEACHER,
      ElementIntents.CONTENT,
      undefined,
      1,
      () => resolvePromise(),
    )

    return { metadata, promise }
  }

  extractSVGString(
    options: { svgIsHidden: boolean } = { svgIsHidden: false },
  ): string {
    this.convertMathAnswersToSVGs()
    const selector = `${options.svgIsHidden ? ".student-helper-assignment-export-hidden " : ""}assignment-sheet svg`
    const svgDOM = this.getDocument().querySelector(selector)?.cloneNode(true)
    if (!svgDOM) throw new Error("No SVG DOM to extract")

    // hide the background border for the math fitbs again otherwise we have double borders in the canvas
    Array.from(
      this.getDocument().getElementsByClassName(
        "pdf-visible",
      ) as HTMLCollectionOf<HTMLElement>,
    ).forEach((rect) => {
      rect.style.visibility = "hidden"
    })

    return this.ExportService.svgDOMtoString(svgDOM)
  }

  private loadMathJaxScript(): Promise<void> {
    return new Promise((resolve) => {
      const mathJaxScript = this.getDocument().getElementById("mathjax-script")
      if (!mathJaxScript) {
        resolve(
          this.loadMathJaxScriptToBody(
            "https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-svg.js",
          ),
        )
      }
      resolve()
    })
  }

  // TODO: import MathJax from node_modules
  private loadMathJaxScriptToBody(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = this.getDocument().createElement("script")
      script.setAttribute("id", "mathjax-script")
      script.setAttribute("src", src)
      script.onload = () => resolve()
      script.onerror = () => reject(new Error("Failed to load MathJax script"))

      const body = this.getDocument().body

      if (body) {
        body.appendChild(script)
      } else {
        reject(new Error("No body found"))
      }
    })
  }

  private convertMathAnswersToSVGs(): void {
    const mathFieldElements = Array.from(
      this.getDocument().getElementsByTagName(
        "math-field",
      ) as HTMLCollectionOf<HTMLInputElement>,
    )

    mathFieldElements.forEach((mathFieldElement, index) => {
      const fos = Array.from(
        this.getDocument().getElementsByTagName("foreignObject"),
      )
      const nodeToAttachTo = fos[index]

      const existingMathSVG = nodeToAttachTo.getElementsByClassName("math-jax")

      // if no existing math answer svg exists, create mathSVG using Math Jax and set all SVG attributes
      if (!existingMathSVG.length) {
        const options = MathJax.getMetricsFor(nodeToAttachTo, true)
        const mathJaxElement = MathJax.tex2svg(mathFieldElement.value, options)

        const mathSVG = mathJaxElement.getElementsByTagName("svg")[0]
        const x = nodeToAttachTo.getAttribute("x")
        const y = nodeToAttachTo.getAttribute("y")
        if (!x || !y) throw new Error("No x or y attribute")
        mathSVG.setAttribute("x", x)
        mathSVG.setAttribute("y", y)
        mathSVG.classList.add("math-jax")

        const heightString = mathSVG.getAttribute("height")
        if (!heightString) throw new Error("No height attribute")
        const height =
          Number(heightString.slice(0, heightString.length - 2)) + 5

        const widthString = mathSVG.getAttribute("width")
        if (!widthString) throw new Error("No width attribute")
        const width = Number(widthString.slice(0, widthString.length - 2)) + 5

        mathSVG.setAttribute("height", `${height}ex`)
        mathSVG.setAttribute("width", `${width}ex`)

        // attach math answer svg to foreign object element
        if (mathSVG) {
          nodeToAttachTo.append(mathSVG)
        }

        const classAttribute = nodeToAttachTo.getAttribute("class")
        if (!classAttribute) throw new Error("No class attribute")
        const questionElementGroups = Array.from(
          this.getDocument().getElementsByClassName(classAttribute.slice(3)),
        )
        if (questionElementGroups.length) {
          questionElementGroups.forEach((question) => {
            // un-hide the svg border in the math answer fitbs and add class name
            const background = question.getElementsByClassName("background")[0]
            if (question.getElementsByClassName("background")[0]) {
              const fitb = background.children[0] as HTMLElement
              fitb.style.visibility = "visible"
              fitb.classList.add("pdf-visible")
            }
          })
        }

        // when exporting the math field element becomes read-only so this is to convert it back
        mathFieldElement.removeAttribute("read-only")
      }

      // hide the add acceptable answer icon
      const addFitbAnswerIcons = Array.from(
        this.getDocument().getElementsByClassName("add-fitb-answer"),
      )
      if (addFitbAnswerIcons.length) {
        addFitbAnswerIcons.map((element) => {
          const previousSibling = element.parentElement
            ?.previousSibling as HTMLElement | null
          if (previousSibling) {
            previousSibling.style.visibility = "hidden"
          }
        })
      }
    })
  }
}
